// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useQuery } from 'apollo-3';

import { ClassroomDrawer } from 'Components/Classroom/Components';
import { getClassroomCourseAttachment } from 'GraphQL/query/Classroom.query';
import { useFaqTracking } from 'Util/Analytics/useFaqTracking';
import { useClassroomContext } from 'Context/ClassroomContext';
import { getSolutionsSummaryByCourse } from 'GraphQL/query/OnlineSolution.query';
import { useClassroomNavigation } from 'Util/Hooks';

import {
  CLEAR_PLUS_TABS,
  useClearPlusByOnDemand,
} from '../../Contexts/ClearPlusByOnDemandProvider';

import ClearPlusHeader from './ClearPlusHeader';
import Attachment from './Attachment';
import OnlineSolution from './OnlineSolution';
import { mapProblemListBySection, mapSectionList } from './util';

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 540,
    borderLeft: 'none',
    backgroundColor: '#FAFAFA',
  },
  paperAnchorBottom: {
    height: '100%',
    backgroundColor: '#FAFAFA',
  },
}));

function ClearPlusPanel({
  openDrawer,
  onCloseDrawer,
  isHorizontalDrawer,
  currentSubVideoId,
  layoutType,
}) {
  const classes = useStyles();
  const [attachmentSelected, setAttachmentSelected] = useState(null);

  const { logClosePanel, logItemClicked, logItemBackButtonClicked } = useFaqTracking();
  const { activeTab, setActiveTab } = useClearPlusByOnDemand();
  const { courseId } = useClassroomContext();
  const { currentSection } = useClassroomNavigation();

  const { data, loading } = useQuery(getClassroomCourseAttachment, {
    variables: { subAssetId: currentSubVideoId },
    skip: !currentSubVideoId,
  });

  const { data: solutionsData } = useQuery(getSolutionsSummaryByCourse, {
    variables: { sourceCourseId: courseId },
    skip: !courseId,
  });

  useEffect(() => {
    setAttachmentSelected(null);
  }, [activeTab]);

  const currentSectionId = useMemo(
    () => (currentSection ? currentSection.referId : ''),
    [currentSection]
  );

  const attachments = useMemo(
    () => (currentSubVideoId && data ? data.classroomCourseAttachment : []),
    [currentSubVideoId, data]
  );

  const { sections, sectionList, problemListBySection } = useMemo(() => {
    const solution = solutionsData?.osSolutionsSummaryByCourse;
    if (!solution) return { sections: [], sectionList: [], problemListBySection: {} };

    const sections = solution.sections ?? [];
    const sectionList = mapSectionList(sections);
    const problemListBySection = mapProblemListBySection(sections);
    return { sections, sectionList, problemListBySection };
  }, [solutionsData?.osSolutionsSummaryByCourse]);

  const handleAttachmentSelected = (attachment) => {
    setAttachmentSelected(attachment);
    logItemClicked({
      faqId: attachment?.id,
      faqTitle: attachment?.title,
    });
  };

  const handleBackToAttachmentList = () => {
    setAttachmentSelected(null);
    logItemBackButtonClicked({
      faqId: attachmentSelected?.id,
      faqTitle: attachmentSelected?.title,
    });
  };

  const handleCloseDrawer = () => {
    setAttachmentSelected(null);
    onCloseDrawer();
    logClosePanel({
      faqId: attachmentSelected?.id,
      faqTitle: attachmentSelected?.title,
    });
  };

  return (
    <ClassroomDrawer
      drawerProps={{
        open: openDrawer,
        horizontalAnchor: ClassroomDrawer.ANCHOR_TYPES.RIGHT,
        horizontal: isHorizontalDrawer,
        classes: {
          paperAnchorRight: classes.drawerPaper,
          paperAnchorBottom: classes.paperAnchorBottom,
        },
      }}
      styleWrapperChildrenProps={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <>
        <ClearPlusHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onCloseDrawer={handleCloseDrawer}
        />
        {activeTab === CLEAR_PLUS_TABS.FAQ && (
          <Attachment
            attachments={attachments}
            attachmentSelected={attachmentSelected}
            backToAttachmentList={handleBackToAttachmentList}
            setAttachmentSelected={handleAttachmentSelected}
            loading={loading}
          />
        )}
        {activeTab === CLEAR_PLUS_TABS.ONLINE_SOLUTION && (
          <OnlineSolution
            sections={sections}
            sectionList={sectionList}
            problemListBySection={problemListBySection}
            currentSectionId={currentSectionId}
          />
        )}
      </>
    </ClassroomDrawer>
  );
}

ClearPlusPanel.propTypes = {
  openDrawer: PropTypes.bool,
  onCloseDrawer: PropTypes.func,
  isHorizontalDrawer: PropTypes.bool,
  toggleComponent: PropTypes.element,
  currentSubVideoId: PropTypes.string,
  layoutType: PropTypes.string,
};

export default ClearPlusPanel;
